import { useEffect, useRef, useState } from 'react'
import AnimalImage from './AnimalImage.jsx'

/**
 * The composed fight scene (SPEC 6.3): A faces right, B is mirrored to face
 * left, a VS badge between them, background tinted by the story's setting.
 * No per-story image generation — every battle is this component.
 *
 * A pose change cross-fades: the outgoing pose stays mounted for one beat under
 * the incoming one, so a swap reads as the animal changing rather than the
 * picture flicking (D-059).
 */
export function settingTheme(setting = '') {
  const s = setting.toLowerCase()
  if (/ice|snow|arctic|frozen|glacier|polar|tundra/.test(s)) return 'ice'
  if (/sea|ocean|water|reef|river|bay|lake|swamp|shallow|deep|tide|pool|shore|marsh/.test(s)) return 'water'
  if (/night|dark|dusk|moon|evening|sunset/.test(s)) return 'night'
  if (/sky|cliff|air|above|cloud/.test(s)) return 'sky'
  if (/forest|jungle|rainforest|tree|wood|canopy|bamboo/.test(s)) return 'forest'
  if (/desert|sand|dune|dry|dust|savanna|plain|grass|field|clearing|prairie/.test(s)) return 'savanna'
  return 'land'
}

/** Keeps the previous pose mounted briefly so the swap can cross-fade. */
function useCrossfade(pose) {
  const [state, setState] = useState({ current: pose, previous: null })
  const timer = useRef(null)
  useEffect(() => {
    setState((s) => (s.current === pose ? s : { current: pose, previous: s.current }))
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setState((s) => ({ current: s.current, previous: null })), 420)
    return () => clearTimeout(timer.current)
  }, [pose])
  return state
}

/**
 * A side is a plain box, except on the matchup at ages 4 and 6, where it is a
 * button: tap an animal to call it as the winner (D-135). The called side wears
 * a "My call" sticker; tapping it again takes the call back. Nothing else about
 * the scene changes, so the battle and the reveal render exactly as before.
 */
/**
 * Every file faces right (D-048). The right-hand fighter is turned to face the
 * left one, and a retreating loser is turned to run AWAY from the winner — so a
 * picture is mirrored when it is on the right XOR it is retreating (D-192, the
 * rule the arena uses, D-190). Before this, a left-hand loser ran straight at
 * the winner. AnimalImage still never mirrors a text-bearing placeholder (D-055).
 */
const mirrorFor = (side, pose) => (side === 'b') !== (pose === 'retreating')

function Side({ animal, pose, side, lunge, called, onCall }) {
  const { current, previous } = useCrossfade(pose)
  const className = `vs__side vs__side--${side} pose--${current}${lunge ? ' lunge' : ''}${onCall ? ' vs__side--callable' : ''}${called ? ' is-called' : ''}`
  const images = (
    <>
      {previous && previous !== current && (
        <AnimalImage key={previous} slug={animal.slug} pose={previous} mirror={mirrorFor(side, previous)} alt="" className="animal-img--outgoing" />
      )}
      <AnimalImage key={current} slug={animal.slug} pose={current} mirror={mirrorFor(side, current)} alt={animal.name} className="animal-img--incoming" />
    </>
  )
  if (!onCall) return <div className={className}>{images}</div>
  return (
    <button type="button" className={className} onClick={() => onCall(animal.slug)} aria-pressed={!!called} aria-label={`${animal.name} wins, I think`}>
      {images}
      {called && <span className="vs__call" aria-hidden="true">⭐ My call</span>}
    </button>
  )
}

export default function VsScene({ a, b, poseA = 'neutral', poseB = 'neutral', setting, badge = 'VS', lungeA = false, lungeB = false, call = null, onCall = null, children }) {
  return (
    <div className={`vs vs--${settingTheme(setting)}${onCall ? ' vs--calling' : ''}`}>
      <Side animal={a} pose={poseA} side="a" lunge={lungeA} called={call === a.slug} onCall={onCall} />
      {badge && <div className="vs__badge" aria-hidden="true">{badge}</div>}
      <Side animal={b} pose={poseB} side="b" lunge={lungeB} called={call === b.slug} onCall={onCall} />
      {children}
    </div>
  )
}
